import { useCallback, useState } from 'react';

import { useAtomValue } from 'jotai';

import { ELVA_SWINGS_PAGE_SIZE, useElvaSwings } from '@queries';
import { activeUserSettingsAtom } from '@store';

import { EmptySwingCard, SwingCard } from '../SwingCard/SwingCard';
import { SwingCardGrid, SwingCardGridPagination } from '../SwingCard/SwingCardList';

import * as css from './ElvaSwingsTab.css';

const INITIAL_PAGE = 1;

type ElvaSwingsTabProps = {
    filter: string;

    setComparisonSwing: (uuid: string) => void;
};

export const ElvaSwingsTab = ({ filter, setComparisonSwing }: ElvaSwingsTabProps) => {
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
    const { data: swings = [], count } = useElvaSwings(currentPage - 1, filter);
    const totalPages = Math.ceil(count / ELVA_SWINGS_PAGE_SIZE) || 1;

    const nextPage = useCallback(() => {
        if (currentPage === totalPages) return;
        setCurrentPage(currentPage + 1);
    }, [currentPage, totalPages]);

    const prevPage = useCallback(() => {
        if (currentPage === 1) return;
        setCurrentPage(currentPage - 1);
    }, [currentPage]);

    return (
        <div className={css.root}>
            <SwingCardGrid
                rowCount={3}
                fullHeight
                emptyListNode={<EmptySwingCard />}
                pagination={
                    <SwingCardGridPagination
                        nextPage={nextPage}
                        prevPage={prevPage}
                        currentPage={currentPage}
                        totalPages={totalPages}
                    />
                }
            >
                {swings.map((swing, index) => (
                    <SwingCard
                        key={swing.uuid + index}
                        {...swing}
                        title={swing.name || 'Untitled swing'}
                        isSelected={swing.uuid === userSettings.comparisonSwingId}
                        onClick={setComparisonSwing}
                    />
                ))}
            </SwingCardGrid>
        </div>
    );
};
